import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";

import EnvPicker from "../crown-data-import/env/env-picker";

const styles = {
  root: {
    width: 260,
    flex: "none",
    borderRight: 1,
    borderColor: "divider",
    p: 2,
    height: "100vh",
    position: "sticky",
    top: 0,
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
  },
  title: { mb: 1.5, px: 0.5 },
  search: { mb: 1 },
  nav: { flex: 1, overflowY: "auto", py: 0 },
  item: { borderRadius: 1 },
  count: { color: "text.secondary", fontVariantNumeric: "tabular-nums" },
  envLabel: { mt: 1.5, mb: 0.5, px: 0.5, display: "block" },
  envHint: { mt: 0.5, display: "block" },
  logout: { mt: 1.5 },
} satisfies Record<string, SxProps<Theme>>;

/** A selectable filter entry in the sidebar (a category or the "all" item). */
export type SidebarItem = {
  /** Label shown and used as the selection key. */
  label: string;
  /** Number of tools in this item. */
  count: number;
};

/** Props for {@link ToolsSidebar}. */
export type ToolsSidebarProps = {
  /** Current search text. */
  readonly query: string;
  /** Called with the new search text. */
  readonly onQueryChange: (value: string) => void;
  /** Filter items to render, in display order. */
  readonly items: SidebarItem[];
  /** Currently selected item label. */
  readonly activeItem: string;
  /** Called with an item label when selected. */
  readonly onSelectItem: (label: string) => void;
  /** Called when the logout button is pressed. */
  readonly onLogout: () => void;
};

/**
 * Left navigation rail for the internal tools launcher, mirroring the Crown
 * data import sidebar: title, search box, a category filter list with counts,
 * the target-environment picker, and a logout action.
 *
 * Search and filter state is owned by the parent via props. The embedded
 * `EnvPicker` self-manages the target env through `EnvProvider`, which the
 * launcher must supply; the env is app-wide (Apollo reads it per request).
 */
const ToolsSidebar = ({
  query,
  onQueryChange,
  items,
  activeItem,
  onSelectItem,
  onLogout,
}: ToolsSidebarProps) => {
  return (
    <Box component="aside" sx={styles.root}>
      <Typography variant="h6" sx={styles.title}>
        Internal tools
      </Typography>
      <TextField
        fullWidth
        size="small"
        placeholder="Search tools…"
        value={query}
        sx={styles.search}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="disabled" fontSize="small" />
            </InputAdornment>
          ),
        }}
        onChange={(event) => {
          onQueryChange(event.target.value);
        }}
      />
      <List sx={styles.nav}>
        {items.map((item) => (
          <ListItemButton
            key={item.label}
            selected={activeItem === item.label}
            sx={styles.item}
            onClick={() => {
              onSelectItem(item.label);
            }}
          >
            <ListItemText primary={item.label} />
            <Typography variant="caption" sx={styles.count}>
              {item.count}
            </Typography>
          </ListItemButton>
        ))}
      </List>
      <Typography variant="caption" sx={styles.envLabel}>
        Target environment
      </Typography>
      <EnvPicker />
      <Typography color="text.secondary" variant="caption" sx={styles.envHint}>
        Applies to all internal tools. Switching may require logging in to that
        environment.
      </Typography>
      <Button variant="contained" sx={styles.logout} onClick={onLogout}>
        Log out
      </Button>
    </Box>
  );
};

export default ToolsSidebar;
